import { EmojiPickerV3 } from "@circle-react-shared/uikit/EmojiPickerV3";
import { getPortalTarget } from "@circle-react-shared/uikit/TipTap/utilities/getPortalTarget";
import { useCommunityFeatureFlagsContext } from "@circle-react/contexts/communityFeatureFlagsContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useCanUploadMedia } from "@circle-react/hooks/useCanUploadMedia";
import { Button } from "@circleco/compass/components/Button";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Tooltip } from "@circleco/compass/components/Tooltip";
import type { Editor } from "@tiptap/react";

import { t } from "@/i18n-js/instance";
import { useFrameDocument } from "@/react/hooks/utils/useFrameDocument";

import { VoiceMessage } from "../VoiceMessage";
import { GifPopover } from "./GifPopover";

export interface MenuBarProps {
  editor: Editor;
  editorHasNoContent: () => boolean;
  handleSubmit: () => void;
  isFocused: boolean;
  isLiveStream?: boolean;
  isSubmitting: boolean;
  isUpdate: boolean;
  isUploading: boolean;
  openFilePicker: () => void;
  portalTargetId?: string | null;
  submitButtonLabel?: string;
  submittingLabel?: string;
}

export const MenuBar = ({
  editor,
  handleSubmit,
  openFilePicker,
  isUploading,
  editorHasNoContent,
  isSubmitting,
  isUpdate,
  isLiveStream = false,
  submitButtonLabel,
  submittingLabel,
  portalTargetId,
}: MenuBarProps) => {
  const {
    record: { voice_messages_enabled: isVoiceMessagesEnabled },
  } = useCommunityFeatureFlagsContext();
  const isMediaUploadAllowed = useCanUploadMedia();
  const { frameDocument, isInFrame } = useFrameDocument();

  const appendTo = portalTargetId
    ? getPortalTarget(portalTargetId, frameDocument)
    : frameDocument?.body || document.body;
  const addMention = () => {
    editor.chain().focus().insertContent(" @").run();
  };

  const addEntity = () => {
    editor.chain().focus().insertContent(" #").run();
  };

  return (
    <div
      className={classNames(
        "flex items-end justify-between",
        submitButtonLabel ? "mt-3" : "mt-2 -ml-2 -mr-1",
      )}
    >
      <div className="flex">
        {isMediaUploadAllowed && (
          <Tooltip side="bottom" content={t("tiptap.attach_file")}>
            <IconButton
              onClick={openFilePicker}
              aria-label={t("tiptap.attach_file")}
              icon="paperclip"
              size="md"
              variant="ghost"
              data-testid="add_attachment_message"
            />
          </Tooltip>
        )}
        {isMediaUploadAllowed && (
          <Tooltip side="bottom" content={t("tiptap.add_image")}>
            <IconButton
              onClick={openFilePicker}
              aria-label={t("tiptap.add_image")}
              icon="image"
              size="md"
              variant="ghost"
              data-testid="add_image_message"
            />
          </Tooltip>
        )}
        {isMediaUploadAllowed && <GifPopover />}
        <EmojiPickerV3
          as="span"
          portalTo={appendTo}
          placement="top-start"
          onEmojiSelect={emojiData => {
            if (!emojiData.emoji) return;
            editor
              .chain()
              .focus()
              .insertContent(emojiData.emoji)
              .insertContent(" ")
              .run();
          }}
          buttonAriaLabel={t("tiptap.add_emoji")}
          popoverPanelClassName={isInFrame ? "max-w-full" : undefined}
          target={
            <IconButton
              icon="emoji-smiley"
              size="md"
              aria-label={t("tiptap.add_emoji")}
              variant="ghost"
            />
          }
        />
        {isVoiceMessagesEnabled && !isLiveStream && !isUpdate && (
          <VoiceMessage />
        )}
        <Tooltip side="bottom" content={t("tiptap.add_entity")}>
          <IconButton
            onClick={addEntity}
            aria-label={t("add_entity")}
            icon="hashtag"
            size="md"
            variant="ghost"
            data-testid="add_entity_list"
          />
        </Tooltip>
        <Tooltip side="bottom" content={t("tiptap.add_mention")}>
          <IconButton
            onClick={addMention}
            aria-label={t("add_mention")}
            icon="at"
            size="md"
            variant="ghost"
            data-testid="add_mention_message"
          />
        </Tooltip>
      </div>
      {submitButtonLabel ? (
        <Button
          type="submit"
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting || editorHasNoContent() || isUploading}
        >
          {isSubmitting
            ? submittingLabel || submitButtonLabel
            : submitButtonLabel}
        </Button>
      ) : (
        <Tooltip side="bottom" content={t("chat_space.send_message")}>
          <IconButton
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting || editorHasNoContent() || isUploading}
            id="tiptap-send"
            variant="primary"
            aria-label={t("chat_space.send_message")}
            loading={isUploading || isSubmitting}
            icon="arrow-up"
            size="md"
            data-testid="submit_button_message"
          />
        </Tooltip>
      )}
    </div>
  );
};
